import { useContext, useEffect, useState } from "react";
import { Route, Routes, BrowserRouter as Router, useLocation, Navigate } from "react-router-dom";
import "./App.css";

import { ToastMsgInit } from "./components/common/ToastMsg";
import { ToastProvider, useToast } from "./context/ToastContext";

// auth imports
import Login from "./pages/auth/Login";
import ForgotPassword from "./pages/auth/ForgotPassword";
import OtpVerification from "./pages/auth/OtpVerification";
import ResetPassword from "./pages/auth/ResetPassword";
import PasswordSuccess from "./pages/auth/PasswordSuccess";

// layout import
import ProtectedRoute from "./routes/ProtectedRoute";
import { AppLayout } from "./components/layout/app-layout";
import { AuthContext, SessionProvider } from "./context/AuthContext";
import { ThemeProvider } from "./components/themeProvider";

// Home
import Home from "./pages/dashboard/Home";

// branch selection (org admin, branch_avail)
import BranchSelect from "./pages/branch/BranchSelect";

// admin imports
import CompanyProfilePage from "./pages/admin/CompanyProfilePage";
import Branch from "./pages/admin/Branch";
import UserGroup from "./pages/admin/UserGroup";
import PasswordPolicy from "./pages/admin/PasswordPolicy";
import UserPermission from "./pages/admin/UserPermission";
import UserPermissionEdit from "./pages/admin/UserPermissionEdit";
import NoticeBoard from "./pages/admin/NoticeBoard";
import Documents from "./pages/admin/Documents";

// master
import EmployeeType from "./pages/master/EmployeeType";
import EmployeeDepartment from "./pages/master/EmployeeDepartment";
import EmployeeDesignation from "./pages/master/EmployeeDesignation";
import EmployeeGroup from "./pages/master/EmployeeGroup";
import Reimbursement from "./pages/master/Reimbursement";
import AssetManagement from "./pages/master/AssetManagement";
import AssetCategory from "./pages/master/AssetCategory";
import AssetSubCategory from "./pages/master/AssetSubCategory";
import LetterType from "./pages/master/LetterType";
import EmployeeLeavePolicy from "./pages/master/EmployeeLeavePolicy";
import LetterTemplate from "./pages/master/LetterTemplate";
import EmployeeLeavePolicyAddEdit from "./pages/master/EmployeeLeavePolicyAddEdit";
import LetterTemplateAddEdit from "./pages/master/LetterTemplateAddEdit";

// configuration
import EmployeeShift from "./pages/configuration/EmployeeShift";
import SubordinateSetup from "./pages/configuration/SubordinateSetup";
import EmployeeShiftAddEdit from "./pages/configuration/EmployeeShiftAddEdit";
import HolidaySetup from "./pages/configuration/HolidaySetup";
import EmployeeWeekOff from "./pages/configuration/EmployeeWeekOff";
import WeekOffAddEdit from "./pages/configuration/WeekOffAddEdit";
import PayrollPeriod from "./pages/configuration/PayrollPeriod";
import Configuration from "./pages/configuration/Configuration";
import SalaryComponent from "./pages/configuration/SalaryComponent";
import SalaryTemplate from "./pages/configuration/SalaryTemplate";
import SalaryTemplateAddEdit from "./pages/configuration/SalaryTemplateAddEdit";

// employee
import UserCreationForm from './pages/employee/helpers/UserCreation';
import EmployeeList from "./pages/employee/EmployeeList";
import EmployeeViewDetailsIndex from "./pages/employee/EmployeeViewDetails";
import MyProfile from "./pages/employee/MyProfile";

// default employee privillages routes
import LeaveRequest from "./pages/employee/LeaveRequest";
import ReimbursementRequest from "./pages/employee/ReimbursementRequest";
import AdvanceRequest from "./pages/employee/AdvanceRequest";
import SubordinatesList from "./pages/employee/SubordinatesList";
import ResignationRequest from "./pages/employee/ResignationRequest";
import ResignationProcess from "./pages/employee/ResignationProcess";

// approval inbox
import ApprovalInbox from "./pages/employee/ApprovalInbox";

// employee onboarding
import EmployeeOnboardingList from "./pages/employee/EmployeeOnboardingList";
import EmployeeOnboardingViewDetailsIndex from "./pages/employee/EmployeeOnboardingViewDetails";

// intern onboarding
import InternOnboardingList from "./pages/employee/InternOnboardingList";
import InternOnboardingViewDetailsIndex from "./pages/employee/InternOnboardingViewDetails";

// payroll
import PayrollProcess from "./pages/payroll/PayrollProcess";
import RunPayrollProcess from "./pages/payroll/RunPayrollProcess";
import ShortDutyReview from "./pages/payroll/ShortDutyReview";
import PaySlips from "./pages/payroll/PaySlips";

// attendance
import DaywiseAttendance from "./pages/attendance/DaywiseAttendance";
import AttendanceLog from "./pages/attendance/AttendanceLog";

// assets
import AssetRegistry from "./pages/assetmanagement/AssetRegistry";
import AssetRegistryAddEdit from "./pages/assetmanagement/AssetRegistryAddEdit";
import AssetEmployeeAudit from "./pages/assetmanagement/AssetEmployeeAudit";
import AssetEmployeeAuditAddEdit from "./pages/assetmanagement/AssetEmployeeAuditAddEdit";

import AssetRequestList from "./pages/assetmanagement/AssetRequestList";
import AssetRequestDetail from "./pages/assetmanagement/AssetRequestDetail";
import AssetRequestSettings from "./pages/assetmanagement/AssetRequestSettings";
import ITHelpDesk from "./pages/employee/ITHelpDesk";

// roster
import Roster from "./pages/roster/Roster";
import RosterAddEdit from "./pages/roster/RosterAddEdit";

// calendar
import Calendar from "./pages/calendar/Calendar";

// training
import TrainingList from "./pages/training/TrainingList";
import TrainingDetail from "./pages/training/TrainingDetail";
import TrainingAddEdit from "./pages/training/TrainingAddEdit";

// appraisal (localStorage-backed demo)
import AppraisalDashboard from "./pages/appraisal/AppraisalDashboard";
import AppraisalCycleList from "./pages/appraisal/AppraisalCycleList";
import AppraisalCycleAddEdit from "./pages/appraisal/AppraisalCycleAddEdit";
import AppraisalCycleDetail from "./pages/appraisal/AppraisalCycleDetail";
import AppraisalKPIManager from "./pages/appraisal/AppraisalKPIManager";
import MyAppraisalList from "./pages/appraisal/MyAppraisalList";
import MyAppraisalDetail from "./pages/appraisal/MyAppraisalDetail";
import TeamReviewList from "./pages/appraisal/TeamReviewList";
import TeamReviewAssign from "./pages/appraisal/TeamReviewAssign";
import TeamReviewDetail from "./pages/appraisal/TeamReviewDetail";
import FinalApprovalList from "./pages/appraisal/FinalApprovalList";
import FinalApprovalDetail from "./pages/appraisal/FinalApprovalDetail";

// reports
import ReportIndex from "./pages/reports/ReportIndex";
import DayWiseAttendance from "./pages/reports/DayWiseAttendance";
import MonthWiseAttendance from "./pages/reports/MonthWiseAttendance";
import SalaryProcessBank from "./pages/reports/SalaryProcessBank";
import EmployeeSalarySummary from "./pages/reports/EmployeeSalarySummary";
import PayrollDetailedSummary from "./pages/reports/PayrollDetailedSummary";
import EmployeeESIStatement from "./pages/reports/EmployeeESIStatement";
import EmployeePFStatement from "./pages/reports/EmployeePFStatement";
import MusterRoll from "./pages/reports/MusterRoll";
import EmployeeEarningsDeduction from "./pages/reports/EmployeeEarningsDeduction";
import AttendanceCorrectionReportView from "./pages/reports/AttendanceCorrectionReportView";
import SubordinatesReport from "./pages/reports/Subordinates";
import RosterReport from "./pages/reports/RosterReport";
import LeavePermissionReport from "./pages/reports/LeavePermissionReport";
import ShortDutyReport from "./pages/reports/ShortDutyReport";

// settings
import SettingsLayout from "./pages/settings/SettingsLayout";

// boundary handler imports
import { NetworkError, NetworkStatusHandler } from "./pages/boundary/NetworkStatus";
import { SplashScreen } from "./pages/boundary/Splash";
import PageNotFound from "./pages/boundary/NotFound";
import SubordinateViewDetails from "./pages/employee/SubordinateViewDetails";
import Security from "./pages/settings/Security";
import Appearance from "./pages/settings/Appearance";
import OrganizationEdit from "./pages/settings/OrganizationEdit";
import SettingsConfiguration from "./pages/settings/SettingsConfiguration";
import AdminDashboard from "./pages/dashboard/AdminDashboard";
import EmployeeTimesheet from "./pages/timesheet/EmployeeTimesheet";
import EmployeeTeamTimesheet from "./pages/timesheet/EmployeeTeamTimesheet";
import Integrations from "./pages/settings/Integrations";
import EmployeeMigration from "./pages/migrations/EmployeeMigration";
import MigrationWizard from "./pages/migrations/MigrationWizard";
import EmedHubIntegration from "./pages/integration/EmedHubIntegration";

// Gate for the Training module — hidden when CustomConfiguration.is_nabh is false
const TrainingGuard = ({ children }) => {
  const customConfig = JSON.parse(sessionStorage.getItem("customConfig") || "{}");
  if (!customConfig.is_nabh) {
    return <Navigate to="/home" replace />;
  }
  return children;
};

// Wrapper to initialize global ToastMsg
const AppWrapper = () => {
  const { showToast } = useToast();

  // Initialize global ToastMsg function
  ToastMsgInit(showToast);

  const ConditionalRouteWrapper = () => {
    const location = useLocation();

    if (location.pathname.endsWith('/subscribenow')) {
      // return <MyplanUpdate />;
      return <span>Your not subscribed!!</span>;
    }

    return <PageNotFound />;
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem('sidebar_preference');
    };
    // Add event listener for before page unload
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Subscribes to AuthContext so it re-evaluates on every login/logout,
  // not just on a hard refresh. Reading sessionStorage outside a context
  // subscription caused stale admin/employee role after switching accounts.
  const RoleAwareHome = () => {
    useContext(AuthContext);
    const token = sessionStorage.getItem("token");
    const is_superAdmin = JSON.parse(sessionStorage.getItem("is_super_admin"));
    const is_Admin = JSON.parse(sessionStorage.getItem("is_admin"));

    if (!token) {
      return <Navigate to={'/'} replace/>
    }

    return (is_superAdmin || is_Admin) ? <AdminDashboard /> : <Home />;
  }

  return (
    <Router>
      <NetworkStatusHandler />
      <Routes>
        {/* Auth routes */}
        <Route path="/" element={<Login />} />
        <Route path="/forgotpassword" element={<ForgotPassword />} />
        <Route path="/otpverification" element={<OtpVerification />} />
        <Route path="/resetpassword" element={<ResetPassword />} />
        <Route path="/passwordsuccess" element={<PasswordSuccess />} />
        {/* Branch selection gate — org admin, full screen, NO sidebar/layout.
            Protected by token but rendered outside AppLayout so no tabs show. */}
        <Route
          path="/branches"
          element={
            <ProtectedRoute>
              <BranchSelect />
            </ProtectedRoute>
          }
        />

        {/* Protected routes */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/home" element={<RoleAwareHome />} />

          {/* admin routes */}
          <Route path="/admin/companyprofile" element={<CompanyProfilePage />} />
          <Route path="/admin/usergroup" element={<UserGroup />} />
          <Route path="/admin/userpermission" element={<UserPermission />} />
          <Route path="/admin/userpermission/add" element={<UserPermissionEdit />} />
          <Route path="/admin/passwordpolicy" element={<PasswordPolicy />} />
          <Route path="/admin/noticeboard" element={<NoticeBoard />} />
          <Route path="/admin/organizationdocuments" element={<Documents />} />

          {/* master */}
          <Route path="/master/employeetype" element={<EmployeeType />} />
          <Route path="/master/department" element={<EmployeeDepartment />} />
          <Route path="/master/designation" element={<EmployeeDesignation />} />
          <Route path="/master/leavetype" element={<EmployeeLeavePolicy />} />
          <Route path="/master/leavetype/add" element={<EmployeeLeavePolicyAddEdit />} />
          <Route path="/master/employeegroup" element={<EmployeeGroup />} />
          <Route path="/master/reimbursementstype" element={<Reimbursement />} />
          <Route path="/master/assettype" element={<AssetManagement />} />
          <Route path="/master/assetcategory" element={<AssetCategory />} />
          <Route path="/master/assetsubategory" element={<AssetSubCategory />} />
          <Route path="/master/lettertype" element={<LetterType />} />
          <Route path="/master/lettertemplate" element={<LetterTemplate />} />
          <Route path="/master/lettertemplate/add" element={<LetterTemplateAddEdit />} />

          {/* configuration */}
          <Route path="/configuration/shiftconfiguration" element={<EmployeeShift />} />
          <Route path="/configuration/shiftconfiguration/add" element={<EmployeeShiftAddEdit />} />
          <Route path="/configuration/holiday" element={<HolidaySetup />} />
          <Route path="/configuration/weekoff" element={<EmployeeWeekOff />} />
          <Route path="/configuration/weekoff/add" element={<WeekOffAddEdit />} />
          <Route path="/configuration/payrollperiod" element={<PayrollPeriod />} />
          <Route path="/configuration/subordinate" element={<SubordinateSetup />} />
          <Route path="/configuration/customconfiguration" element={<Configuration />} />
          <Route path="/configuration/salarycomponent" element={<SalaryComponent />} />
          <Route path="/configuration/salarytemplate" element={<SalaryTemplate />} />
          <Route path="/configuration/salarytemplate/add" element={<SalaryTemplateAddEdit />} />

          {/* employee */}
          <Route path="/employee" element={<EmployeeList />} />
          <Route path="/employee/add" element={<UserCreationForm />} />
          <Route path="/employee/viewDetails" element={<EmployeeViewDetailsIndex />} />

          <Route path="/employee/onboarding" element={<EmployeeOnboardingList />} />
          <Route path="/candidate/add" element={<UserCreationForm />} />
          <Route path="/employee/onboarding/viewDetails" element={<EmployeeOnboardingViewDetailsIndex />} />

          <Route path="/employee/internonboarding" element={<InternOnboardingList />} />
          <Route path="/intern/add" element={<UserCreationForm />} />
          <Route path="/intern/onboarding/viewDetails" element={<InternOnboardingViewDetailsIndex />} />

          {/* default employee privillages routes */}
          <Route path="/employeeprofile" element={<MyProfile />} />
          <Route path="/employee/leaverequest" element={<LeaveRequest />} />
          <Route path="/employee/reimbursementrequest" element={<ReimbursementRequest />} />
          <Route path="/employee/advancerequest" element={<AdvanceRequest />} />
          <Route path="/ithelpdesk" element={<ITHelpDesk />} />
          <Route path="/employee/subordinate" element={<SubordinatesList />} />
          <Route path="/employee/subordinate/viewDetails" element={<SubordinateViewDetails />} />
          <Route path="/employee/resignationrequest" element={<ResignationRequest />} />
          <Route path="/employee/resignationrequest/process" element={<ResignationProcess />} />
          <Route path="/employee/approvalinbox" element={<ApprovalInbox />} />

          {/* timesheet */}
          <Route path="/employee/timesheet" element={<EmployeeTimesheet />} />
          <Route path="/employee/team/timesheet" element={<EmployeeTeamTimesheet />} />

          {/* payroll */}
          <Route path="/payroll/payrollprocess" element={<PayrollProcess />} />
          <Route path="/payroll/shortdutyreview" element={<ShortDutyReview />} />
          <Route path="/payroll/runpayrollprocess" element={<RunPayrollProcess />} />
          <Route path="/payroll/payslips" element={<PaySlips />} />

          {/* attendance */}
          <Route path="/attendance/dayattendance" element={<DaywiseAttendance />} />
          <Route path="/attendance/attendancelog" element={<AttendanceLog />} />
          {/* <Route path="/attendance/attendancelogChart" element={<h1>Attendance Log Chart</h1>} />  // attendance chart need to work */}

          {/* assets Management */}
          <Route path="/assetmanagement/assetregistry" element={<AssetRegistry />} />
          <Route path="/assetmanagement/assetregistry/add" element={<AssetRegistryAddEdit />} />
          <Route path="/assetmanagement/assetemployeeaudit" element={<AssetEmployeeAudit />} />
          <Route path="/assetmanagement/assetemployeeaudit/add" element={<AssetEmployeeAuditAddEdit />} />
          {/* asset requests / tickets (admin) */}
          <Route path="/assetmanagement/assetrequest" element={<AssetRequestList />} />
          <Route path="/assetmanagement/assetrequest/:id" element={<AssetRequestDetail />} />
          <Route path="/assetmanagement/settings" element={<AssetRequestSettings />} />

          {/* roster */}
          <Route path="/roster" element={<Roster />} />
          <Route path="/roster/add" element={<RosterAddEdit />} />

          {/* calendar */}
          <Route path="/calendar" element={<Calendar />} />

          {/* training (gated by CustomConfiguration.is_nabh) */}
          <Route path="/training" element={<TrainingGuard><TrainingList /></TrainingGuard>} />
          <Route path="/training/overdue" element={<TrainingGuard><TrainingList defaultFilter="overdue" /></TrainingGuard>} />
          <Route path="/training/team" element={<TrainingGuard><TrainingList mode="team" /></TrainingGuard>} />
          <Route path="/training/add" element={<TrainingGuard><TrainingAddEdit /></TrainingGuard>} />
          <Route path="/training/edit/:id" element={<TrainingGuard><TrainingAddEdit /></TrainingGuard>} />
          <Route path="/training/view/:id" element={<TrainingGuard><TrainingDetail mode="assignment" /></TrainingGuard>} />
          <Route path="/training/task/:id" element={<TrainingGuard><TrainingDetail mode="task" /></TrainingGuard>} />

          {/* appraisal */}
          <Route path="/appraisal" element={<AppraisalDashboard />} />
          <Route path="/appraisal/cycles" element={<AppraisalCycleList />} />
          <Route path="/appraisal/cycles/new" element={<AppraisalCycleAddEdit />} />
          <Route path="/appraisal/cycles/edit/:id" element={<AppraisalCycleAddEdit />} />
          <Route path="/appraisal/cycles/:id" element={<AppraisalCycleDetail />} />
          <Route path="/appraisal/kpi-manager" element={<AppraisalKPIManager />} />
          <Route path="/appraisal/my-appraisal" element={<MyAppraisalList />} />
          <Route path="/appraisal/my-appraisal/:id" element={<MyAppraisalDetail />} />
          <Route path="/appraisal/team-review" element={<TeamReviewList />} />
          <Route path="/appraisal/team-review/assign" element={<TeamReviewAssign />} />
          <Route path="/appraisal/team-review/:id" element={<TeamReviewDetail />} />
          <Route path="/appraisal/final-approval" element={<FinalApprovalList />} />
          <Route path="/appraisal/final-approval/:id" element={<FinalApprovalDetail />} />

          {/* reports */}
          <Route path="/reports" element={<ReportIndex />} />
          <Route path="/reports/daywise-attendance" element={<DayWiseAttendance />} />
          <Route path="/reports/monthwise-attendance" element={<MonthWiseAttendance />} />
          <Route path="/reports/salary-process-bank" element={<SalaryProcessBank />} />
          <Route path="/reports/employee-salary-summary" element={<EmployeeSalarySummary />} />
          <Route path="/reports/payroll-detailed-summary" element={<PayrollDetailedSummary />} />
          <Route path="/reports/employee-esi-statement" element={<EmployeeESIStatement />} />
          <Route path="/reports/employee-pf-statement" element={<EmployeePFStatement />} />
          <Route path="/reports/muster-roll" element={<MusterRoll />} />
          <Route path="/reports/employee-credits-debits" element={<EmployeeEarningsDeduction />} />
          <Route path="/reports/attendance-correction" element={<AttendanceCorrectionReportView />} />
          <Route path="/reports/subordinates-report" element={<SubordinatesReport />} />
          <Route path="/reports/roster-report" element={<RosterReport />} />
          <Route path="/reports/leave-permission-report" element={<LeavePermissionReport />} />
          <Route path="/reports/short-duty" element={<ShortDutyReport />} />

          <Route path="/datamigration/employeemigration" element={<EmployeeMigration />} />
          <Route path="/datamigration/employeemigration/new" element={<MigrationWizard />} />
          <Route path="/datamigration/employeemigration/batch/:id" element={<MigrationWizard />} />
          {/* eMedHub integration (operational screen; connection configured under Settings) */}
          <Route path="/integration" element={<EmedHubIntegration />} />

          {/* future todo list */}
          <Route path="settings" element={<SettingsLayout />}>
            <Route index element={<Navigate to="appearance" replace />} />
            <Route path="security" element={<Security />} />
            <Route path="integrations" element={<Integrations />} />
            <Route path="appearance" element={<Appearance />} />
            <Route path="organization-edit" element={<OrganizationEdit />} />
            <Route path="branch" element={<Branch />} />
            <Route path="configuration" element={<SettingsConfiguration />} />
          </Route>

          <Route path="*" element={<ConditionalRouteWrapper />} />
          <Route path="/networkError" element={<NetworkError />} />
        </Route>
      </Routes>
    </Router>
  );
};

function App() {
  const [loading, setLoading] = useState(true);
  return (
    <ThemeProvider>
      <SessionProvider>
        <ToastProvider>
          {
            loading ? <SplashScreen setLoading={setLoading} /> : <AppWrapper />
          }
        </ToastProvider>
      </SessionProvider>
    </ThemeProvider>
  );
}

export default App;
